import { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';

export default function RolesIndex({ roles }) {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);

    const protectedRoles = ['Super Admin', 'Admin', 'Manager', 'Agent', 'Viewer'];

    const handleDelete = () => {
        router.delete(`/roles/${roleToDelete.id}`, {
            onSuccess: () => {
                setShowDeleteModal(false);
                setRoleToDelete(null);
            },
        });
    };

    const getRoleBadgeClass = (roleName) => {
        switch (roleName) {
            case 'Super Admin':
                return 'bg-danger';
            case 'Admin':
                return 'bg-warning text-dark';
            case 'Manager':
                return 'badge-primary';
            case 'Agent':
                return 'badge-info';
            case 'Viewer':
                return 'badge-secondary';
            default:
                return 'bg-secondary';
        }
    };

    return (
        <AppLayout title="Roles">
            <PageHeader
                title="Roles"
                subtitle="Manage user roles and permissions"
                breadcrumbs={[
                    { label: 'Settings' },
                    { label: 'Roles' },
                ]}
                actions={
                    <Link href="/roles/create" className="btn btn-primary">
                        <i className="bi bi-plus-lg"></i>
                        <span>Add Role</span>
                    </Link>
                }
            />

            <div className="row">
                <div className="col-lg-8">
                    <div className="card">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Role</th>
                                        <th className="text-center">Users</th>
                                        <th className="text-center">Permissions</th>
                                        <th>Created</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {roles.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="text-center py-5">
                                                <div className="text-muted">
                                                    <i className="bi bi-shield fs-1 d-block mb-3"></i>
                                                    No roles found
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        roles.map((role) => (
                                            <tr key={role.id}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div
                                                            className="avatar"
                                                            style={{
                                                                background: role.name === 'Super Admin' ? 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)' :
                                                                    role.name === 'Admin' ? 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)' :
                                                                    'linear-gradient(135deg, #2563eb 0%, #8b5cf6 100%)',
                                                                color: 'white',
                                                            }}
                                                        >
                                                            <i className="bi bi-shield-check"></i>
                                                        </div>
                                                        <div>
                                                            <div className="fw-semibold">{role.name}</div>
                                                            {protectedRoles.includes(role.name) && (
                                                                <span className="badge badge-secondary small">System Role</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <span className="badge bg-light text-dark">
                                                        {role.users_count} {role.users_count === 1 ? 'user' : 'users'}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span className="badge badge-primary">
                                                        {role.permissions_count} permissions
                                                    </span>
                                                </td>
                                                <td className="text-muted">
                                                    {role.created_at}
                                                </td>
                                                <td className="text-end">
                                                    <div className="btn-group">
                                                        <Link
                                                            href={`/roles/${role.id}/edit`}
                                                            className="btn btn-sm btn-outline-secondary"
                                                        >
                                                            <i className="bi bi-pencil"></i>
                                                        </Link>
                                                        {!protectedRoles.includes(role.name) && (
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => {
                                                                    setRoleToDelete(role);
                                                                    setShowDeleteModal(true);
                                                                }}
                                                                disabled={role.users_count > 0}
                                                                title={role.users_count > 0 ? 'Cannot delete role with assigned users' : 'Delete role'}
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="col-lg-4">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Role Hierarchy</h5>
                        </div>
                        <div className="card-body">
                            <div className="mb-3 pb-3 border-bottom">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <span className="badge bg-danger">Super Admin</span>
                                    <i className="bi bi-arrow-right text-muted"></i>
                                    <small className="text-muted">Full Access</small>
                                </div>
                                <small className="text-muted d-block">Complete system control including user and role management</small>
                            </div>
                            <div className="mb-3 pb-3 border-bottom">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <span className="badge bg-warning text-dark">Admin</span>
                                    <i className="bi bi-arrow-right text-muted"></i>
                                    <small className="text-muted">Administrative</small>
                                </div>
                                <small className="text-muted d-block">Full access except role management permissions</small>
                            </div>
                            <div className="mb-3 pb-3 border-bottom">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <span className="badge badge-primary">Manager</span>
                                    <i className="bi bi-arrow-right text-muted"></i>
                                    <small className="text-muted">Management</small>
                                </div>
                                <small className="text-muted d-block">Manage CRM modules, travel operations, and view reports</small>
                            </div>
                            <div className="mb-3 pb-3 border-bottom">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <span className="badge badge-info">Agent</span>
                                    <i className="bi bi-arrow-right text-muted"></i>
                                    <small className="text-muted">Operations</small>
                                </div>
                                <small className="text-muted d-block">Create and manage bookings, contacts, and daily operations</small>
                            </div>
                            <div>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <span className="badge badge-secondary">Viewer</span>
                                    <i className="bi bi-arrow-right text-muted"></i>
                                    <small className="text-muted">Read Only</small>
                                </div>
                                <small className="text-muted d-block">View-only access to all modules without edit capabilities</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Modal */}
            {showDeleteModal && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show d-block" tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Delete Role</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowDeleteModal(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <p>Are you sure you want to delete the <strong>{roleToDelete?.name}</strong> role?</p>
                                    <p className="text-muted small mb-0">This action cannot be undone.</p>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowDeleteModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={handleDelete}
                                    >
                                        Delete Role
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </AppLayout>
    );
}
